import { ScrollView, View } from "@-/com.npmjs.react-native";
import {
  useGlassHeaderInset,
  useHeaderScrollContentStyle,
} from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { DeckCardListSectionSkeleton } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section";
import {
  DeckCardSurfaceFrame,
  DeckCardSurfaceScrollView,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.surface";
import { useIsActorSyncPreparing } from "@-/dev.improve.haniverse.routes.nav._context";
import { useSyncStatus } from "@-/dev.improve.haniverse.lib.sync-client";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { useLocalDayKey } from "@-/dev.improve.haniverse.lib.time.local-day";
import { useMemo } from "react";
import { buildDeckUpcomingSchedule, deckUpcomingRangeLabel } from "#build-deck-upcoming-schedule";
import { DeckUpcomingReviewChart } from "#deck-upcoming-review-chart";
import { DeckUpcomingScheduleLayout } from "#deck-upcoming-schedule-layout";

export function DeckUpcomingPageLoadingFallback({ today = false }: { today?: boolean } = {}) {
  const todayKey = useLocalDayKey();
  const schedule = useMemo(
    () => buildDeckUpcomingSchedule([], new Date(`${todayKey}T00:00:00`)),
    [todayKey],
  );
  const isSyncing = useIsActorSyncPreparing();
  const hasHydrated = useHasHydrated();
  const { syncedEntryCount } = useSyncStatus();
  const topInset = useGlassHeaderInset() ?? 0;
  const contentContainerStyle = useHeaderScrollContentStyle({
    contentContainerStyle: { paddingBottom: 4 },
    headerOverlap: topInset,
  });
  const loadingCards =
    hasHydrated && isSyncing ? (
      <View className="gap-2 p-4">
        <Text>Syncing data from cloud...</Text>
        {syncedEntryCount == null ? null : (
          <Text className="text-sm text-muted-foreground">
            {syncedEntryCount.toLocaleString()} entries available locally
          </Text>
        )}
      </View>
    ) : (
      <DeckCardListSectionSkeleton
        cardCount={6}
        characterCount={6}
        displayMode={today ? "morpheme-details-tiles" : "morpheme-glyph-tiles"}
      />
    );
  if (today) {
    return (
      <DeckCardSurfaceScrollView testID="deck-today-loading">
        <DeckCardSurfaceFrame
          accessibilityLabel="Loading today's reviews"
          countLabel="Reviews"
          subtitle="Due now or later today"
          testID="deck-today-loading-surface"
          title="Today"
        >
          {loadingCards}
        </DeckCardSurfaceFrame>
      </DeckCardSurfaceScrollView>
    );
  }
  return (
    <DeckUpcomingScheduleLayout
      chart={(onControlsHeightChange) => (
        <DeckUpcomingReviewChart
          isTodayInView
          loading
          maximumCardCount={0}
          onControlsHeightChange={onControlsHeightChange}
          onNavigate={() => {}}
          onPan={() => {}}
          onSelect={() => {}}
          onSelectSpan={() => {}}
          onTimeframeChange={() => {}}
          onToday={() => {}}
          overdueCount={0}
          periods={schedule.periods}
          rangeEnd={schedule.rangeEnd}
          rangeStart={schedule.rangeStart}
          selectedKey={schedule.selected.key}
          timeframe="week"
          todayKey={todayKey}
          unscheduledCount={0}
        />
      )}
      contentKey="loading"
    >
      {(bottomInset) => (
        <ScrollView
          className="flex-1"
          contentContainerStyle={[contentContainerStyle, { paddingBottom: bottomInset }]}
          contentInsetAdjustmentBehavior="never"
        >
          <DeckCardSurfaceFrame
            accessibilityLabel="Loading review schedule"
            countLabel="Upcoming"
            testID="deck-upcoming-loading-surface"
            title={deckUpcomingRangeLabel(schedule.rangeStart, schedule.rangeEnd, "week")}
          >
            {loadingCards}
          </DeckCardSurfaceFrame>
        </ScrollView>
      )}
    </DeckUpcomingScheduleLayout>
  );
}
